import Image from 'next/image';
import {
  Children,
  createElement,
  Fragment,
  isValidElement,
  NamedExoticComponent,
  useMemo,
} from 'react';
import flattenChildren from 'react-keyed-flatten-children';
import rehypeHighlight from 'rehype-highlight';
import rehypeParse from 'rehype-parse/lib';
import rehypeReact from 'rehype-react';
import rehypeSlug from 'rehype-slug';
import { unified } from 'unified';
import {
  rehypeTableOfContents,
  TableOfContentsOutput,
} from './rehypeTableOfContents';

const Img = ({ src, alt, width = '100%' }: JSX.IntrinsicElements['img']) => {
  if (!src || !alt) throw new Error('Image must have `src` and `alt`');

  return (
    <Image
      src={src}
      alt={alt}
      width={1200}
      height={1200}
      style={{ width }}
      className="block h-auto my-6 max-md:!w-full rounded-xl"
    />
  );
};

Img.displayName = 'BlogImg';

const isImageNode = (child: React.ReactNode) => {
  return (
    child &&
    typeof child === 'object' &&
    'type' in child &&
    typeof child.type !== 'string' &&
    (child.type as NamedExoticComponent).displayName === 'BlogImg'
  );
};

const Table = ({ children }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full border-collapse border border-plum-200 text-fog-900 table-auto">{children}</table>
  </div>
);

const TableRow = ({ children }) => (
  <tr className="even:bg-plum-100">{children}</tr>
);

const TableHeader = ({ children }) => (
  <th className="p-3 bg-plum-100 text-fog-900">{children}</th>
);

const TableCell = ({ children }) => (
  <td className="p-3 border border-plum-200 text-fog-900">{children}</td>
);

// Adjusts browser default scroll to headings to account for sticky nav.
const HEADING_PADDING_CLASS = 'pt-20 -mt-20 md:pt-24 md:-mt-24';

export const useProcessor = (html: string) => {
  return useMemo(() => {
    const tableOfContents = {} as TableOfContentsOutput;
    const output = unified()
      .use(rehypeParse, { fragment: true })
      .use(rehypeSlug)
      .use(rehypeTableOfContents, tableOfContents)
      .use(rehypeHighlight)
      .use(rehypeReact, {
        createElement,
        Fragment,
        components: {
          a: ({ href, children }: JSX.IntrinsicElements['a']) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="font-semibold underline text-plum-950 underline-offset-[3px] hover:text-plum-800 transition-colors"
            >
              {children}
            </a>
          ),
          h1: () => {
            throw new Error(
              '`h1` (#) is reserved for the blog post title. Use `h2` (##) and below instead.'
            );
          },
          h2: ({ children, id }: JSX.IntrinsicElements['h2']) => (
            <div
              className={`${HEADING_PADDING_CLASS} [&:first-child>*]:mt-0`}
              id={id}
            >
              <h2
                className={`font-sans font-medium text-2xl text-plum-950 mb-6 mt-6`}
              >
                {children}
              </h2>
            </div>
          ),
          h3: ({ children, id }: JSX.IntrinsicElements['h2']) => (
            <div className={HEADING_PADDING_CLASS} id={id}>
              <h3
                className={`font-sans font-medium text-xl text-plum-950 mb-6 mt-2`}
              >
                {children}
              </h3>
            </div>
          ),
          h4: ({ children, id }: JSX.IntrinsicElements['h2']) => (
            <div className={HEADING_PADDING_CLASS} id={id}>
              <h4
                className={`font-sans font-medium text-lg text-plum-950 my-6`}
              >
                {children}
              </h4>
            </div>
          ),
          p: (props: JSX.IntrinsicElements['p']) => {
            // Avoid wrapping images in unnecessary paragraph tags.
            if (props.children && typeof props.children !== 'string') {
              if (
                isImageNode(props.children) ||
                (Array.isArray(props.children) &&
                  isImageNode(props.children[0]))
              )
                return <>{props.children}</>;
            }

            return (
              <p className="font-sans text-base text-fog-900 leading-relaxed mb-6">
                {props.children}
              </p>
            );
          },
          strong: ({ children }: JSX.IntrinsicElements['strong']) => (
            <strong className="text-plum-950">{children}</strong>
          ),
          ul: ({ children }: JSX.IntrinsicElements['ul']) => (
            <ul className="flex flex-col gap-6 text-fog-900 my-6">
              {Children.map(
                flattenChildren(children).filter(isValidElement),
                (child, index) => (
                  <li key={index} className="flex gap-2 items-start">
                    <span className="w-1 h-1 rounded-full bg-current block shrink-0 mt-1" />
                    {child}
                  </li>
                )
              )}
            </ul>
          ),
          ol: ({ children }: JSX.IntrinsicElements['ol']) => (
            <ol className="flex flex-col gap-6 text-fog-900 my-6">
              {Children.map(
                flattenChildren(children).filter(isValidElement),
                (child, index) => (
                  <li key={index} className="flex gap-2 items-start">
                    <span
                      className="font-sans text-base text-plum-950 font-semibold shrink-0 min-w-[1.4ch]"
                      aria-hidden
                    >
                      {index + 1}.
                    </span>
                    {child}
                  </li>
                )
              )}
            </ol>
          ),
          li: ({ children }: JSX.IntrinsicElements['li']) => (
            <span className="font-sans text-base leading-relaxed">
              {children}
            </span>
          ),
          code: ({ children, className }: JSX.IntrinsicElements['code']) => {
            // A hack to detect if the code block is a language block wrapped in a `pre` tag
            // since highlight.js adds a `className`.
            if (className) return <code className={className}>{children}</code>;
            return (
              <code className="inline-block font-code text-base bg-plum-100 text-fog-950 p-1 -my-1 rounded">
                {children}
              </code>
            );
          },
          pre: ({ children }: JSX.IntrinsicElements['pre']) => (
            <pre className="p-4 lg:p-8 rounded-2xl shadow-lg bg-fog-50 mb-6 [&>code.hljs]:p-0 [&>code.hljs]:bg-transparent font-code">
              {children}
            </pre>
          ),
          em: ({ children }: JSX.IntrinsicElements['em']) => (
            <em className="not-italic -font-variant-slant-10">{children}</em>
          ),
          img: Img,
          blockquote: ({ children }: JSX.IntrinsicElements['blockquote']) => (
            <blockquote className="text-lg font-semibold text-plum-950 bg-plum-100 p-4 rounded-xl">
              {children}
            </blockquote>
          ),
          table: Table,
          tr: TableRow,
          th: TableHeader,
          td: TableCell,
        },
      })
      .processSync(html);

    return {
      content: output.result,
      tableOfContents: tableOfContents.result,
    };
  }, [html]);
};
